<script>
import api from '@utils/api'
import axios from 'axios'
import Layout from '@layouts/main'
import formatDate from '@utils/format-date-with-time'
import getStatusLabel from '@utils/status-labels'
import TablePaginator from '@components/paginator'
import OilLevelIcon from '@components/oil-level-icon'
import BatteryLevelIcon from '@components/battery-level-icon'
import AlertTableItem from '@components/alert-table-item'

import customTable from '@utils/mixins/custom-table'
import paginationMethods from '@utils/mixins/pagination-methods'
import userHasRolesToWrite from '@utils/userPermissions'

import CreateDeviceDialog from '@components/create-device-dialog'
import EditDeviceDialog from '@components/edit-device-dialog'
import DeviceTableActions from '@components/device-table-actions'
import DialogShipConfirm from '@components/dialog-ship-confirm'

import { mapGetters } from 'vuex'
import AdjustColumnsDialog from '@components/adjust-columns-dialog'
import ConfirmationDialog from '@components/confirmation-dialog'
import { hideValues, updateColumns } from '@utils/helpers/columns'
import { convertBatteryCharge } from '@utils/converter'

export default {
  name: 'Devices',
  metaInfo: {
    title: 'devices',
    meta: [{ name: 'description', content: 'Devices' }],
  },
  components: {
    Layout,
    TablePaginator,
    BatteryLevelIcon,
    OilLevelIcon,
    CreateDeviceDialog,
    EditDeviceDialog,
    DeviceTableActions,
    DialogShipConfirm,
    AdjustColumnsDialog,
    ConfirmationDialog,
    AlertTableItem,
  },
  mixins: [customTable, paginationMethods],
  data() {
    return {
      headers: [
        { text: 'workshop', value: 'workshopId', sortable: true },
        { text: 'serial', value: 'serial', sortable: true },
        {
          text: 'alerts',
          value: 'alerts',
          show: true,
        },
        {
          text: 'transmitter_id',
          value: 'transmitterId',
          sortable: true,
          show: true,
        },
        { text: 'oilLevel', value: 'oilLevel', sortable: true, show: true },
        { text: 'oil', value: 'oil', sortable: true, show: true },
        { text: 'drum', value: 'drum', sortable: true, show: true },
        { text: 'status', value: 'status', sortable: true, show: true },
        {
          text: 'lastMeasurement',
          value: 'lastMeasurement',
          sortable: true,
          show: true,
        },
        {
          text: 'shipmentDate',
          value: 'shipmentDate',
          sortable: true,
          show: true,
        },
        {
          text: 'sufficiency',
          value: 'sufficiency',
          sortable: true,
          show: true,
        },
        { text: 'battery', value: 'batteryLevel', sortable: true, show: true },
        { text: 'connection', value: 'connection', sortable: true, show: true },
      ],
      items: [],
      isLoading: false,
      page: 1,
      fab: false,
      rolesAllowedToCreate: ['SUPERUSER'],
      hasFilterModal: false,
      showFiltersModal: false,
      showCreateDeviceModal: false,
      showEditDeviceModal: false,
      sortBy: 'serial',
      needConfirm: false,
      currentItem: {},
      clonedItem: null,
      modelName: 'Devices',
      showDialog: false,
      showConfirmationDialog: false,
      confirmationDialogText: '',
      confirmationDialogBtnColor: 'error',
      confirmationDialogBtnText: 'Apply',
    }
  },
  computed: {
    ...mapGetters({
      devices: 'data/getDevices',
    }),
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filters,
      }
    },
    userAllowedToCreate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToCreate
      )
    },
    showBatteryIcon() {
      return (
        this.$store.getters['userSettings/get']('batteryChargeDisplay') ===
        'percentage'
      )
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: 'devices',
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    if (
      userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
    ) {
      this.headers.push({
        text: 'actions',
        value: 'actions',
        sortable: false,
      })
    }

    this.getPaginator('devices')
    this.getFilters()
    this.fetchData()
  },
  methods: {
    getStatusLabel,
    dateFormat(date) {
      const formatedDate = formatDate(date)
      return formatedDate === 'Invalid Date' ? '-' : formatedDate
    },
    async fetchData() {
      this.isLoading = true

      const payload = {
        params: this.params,
      }

      this.$store.dispatch('data/fetchDevices', { payload }).then(response => {
        this.totalPages = response.totalPages
        this.first = response.first
        this.last = response.last
        this.page = response.number + 1
        this.pagination = {
          ...this.pagination,
          page: 1,
          rowsPerPage: response.size,
          sortBy: this.sortBy,
          descending: this.descending,
        }
        this.isLoading = false
      })
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters']('devices')
      this.filters = filter ? filter.value : null
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value ? Number(value) : value
      )
    },
    onCreateHandler() {
      this.showCreateDeviceModal = false
      this.showEditDeviceModal = false
      this.fetchData()
    },
    onShipHandler() {
      this.needConfirm = false
      this.fetchData()
    },
    handleActionsClicked(...args) {
      const [eventName, currentItem] = args
      this.currentItem = currentItem
      this.clonedItem = null
      this.currentEventName = eventName
      if (eventName === 'clone' || eventName === 'edit') {
        this.onDialogApplyClicked()
        return
      }

      this.confirmationDialogText =
        this.$t(`titles__${eventName}_device`) + ` ${this.currentItem.serial} ?`
      this.confirmationDialogBtnText = this.$t(`buttons__${eventName}`)

      this.showConfirmationDialog = true
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
    getSufficiency(sufficiency) {
      if (!sufficiency && sufficiency === 0) {
        return this.$t('label__indefinably')
      } else return sufficiency + ' ' + this.$t('label__days')
    },
    getConnection(connection) {
      if (connection == null) {
        return ''
      }
      if (connection) {
        return this.$t('label__true')
      } else return this.$t('label__false')
    },
    onDialogApplyClicked() {
      this.showConfirmationDialog = false
      switch (this.currentEventName) {
        case 'ship':
          axios
            .post(`/api/1/devices/ship`, {
              serial: this.currentItem.serial,
              toOrganisation: this.currentItem.workshopId,
              confirm: false,
            })
            .then(response => {
              this.fetchData()
            })
            .catch(error => {
              const responseCode = error.response.data.code
              if (responseCode === '8') {
                // Need to confirm finalization
                this.needConfirm = true
              } else {
                this.needConfirm = false
                api.handleError(error)
                // this.$emit('close-finalize-modal')
              }
            })
          break
        case 'writeoff':
          axios
            .post('/api/1/devices/writeoff', {
              serial: this.currentItem.serial,
            })
            .then(res => {
              this.fetchData()
            })
          break
        case 'return':
          axios
            .post('/api/1/devices/return', { serial: this.currentItem.serial })
            .then(res => {
              this.fetchData()
            })
          break
        case 'clone':
          this.clonedItem = this.currentItem
          this.showCreateDeviceModal = true
          break
        case 'edit':
          this.clonedItem = { ...this.currentItem }
          this.showEditDeviceModal = true
          break
        default:
          break
      }
    },
    onDialogCloseClicked() {
      this.currentItem = null
      this.currentEventName = null
      this.showConfirmationDialog = false
    },
    convertBatteryCharge(value, withoutUnitSymbol) {
      return convertBatteryCharge(
        value,
        this.$store.getters['userSettings/get']('batteryChargeDisplay'),
        withoutUnitSymbol
      )
    },
    getItem(dictionary, id) {
      return this.$store.getters['data/getItemFromDictionary'](dictionary, id)
    },
  },
}
</script>

<template>
  <Layout>
    <confirmation-dialog
      v-model="showConfirmationDialog"
      :btn-props="{
        text: confirmationDialogBtnText,
        color: confirmationDialogBtnColor
      }"
      @apply-clicked="onDialogApplyClicked"
      @close-clicked="onDialogCloseClicked"
    >
      {{ confirmationDialogText }}
    </confirmation-dialog>
    <div slot="actionButton">
      <div class="hidden-xs-only">
        <v-btn
          color="info"
          @click="fetchData"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn
          v-if="hasFilterModal"
          :color="filters ? `success` : `info`"
          @click="showFiltersModal = true"
        >
          <v-icon>filter_list</v-icon>
        </v-btn>
        <v-btn
          v-if="userAllowedToCreate"
          slot="activator"
          color="primary"
          dark
          @click.native.stop="clonedItem = null; showCreateDeviceModal = true"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </div>
      <div class="hidden-sm-and-up">
        <v-speed-dial
          v-model="fab"
          direction="left"
          transition="slide-y-reverse-transition"
        >
          <v-btn
            slot="activator"
            v-model="fab"
            color="blue darken-2"
            dark
            fab
            small
            flat
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
          <v-btn
            color="primary"
            dark
            small
            fab
            @click.native.stop="showCreateDeviceModal = true"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            v-if="hasFilterModal"
            :color="filters ? `success` : `info`"
            small
            fab
            @click="showFiltersModal = true"
          >
            <v-icon>filter_list</v-icon>
          </v-btn>
          <v-btn
            color="info"
            small
            fab
            @click="fetchData"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-speed-dial>
      </div>
    </div>
    <v-layout justify-end>
      <v-btn
        icon
        flat
        @click="openDialog()">
        <v-icon>view_column</v-icon>
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="devices"
      :class="$style.sdTable"
      :loading="isLoading"
      disable-initial-sort
      item-key="serial"
      hide-actions
    >
      <template
        slot="headers"
        slot-scope="{ headers }">
        <tr>
          <th
            v-for="header in headers"
            :key="header.text"
            :class="getSortClass(header)"
            :width="header.width ? header.width : ''"
            :style="[header.show === false ? {'display': 'none'} : {'text-align': 'left'}]"
            @click="changeSort(header)"
          >
            {{ $t(`label__${header.text}`) }}
            <v-icon
              v-if="header.sortable"
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <!-- eslint-disable-next-line no-unused-vars -->
      <template
        slot="items"
        slot-scope="{ item }"
      >
        <tr :class="$style.sdEditRow">
          <td><router-link
            v-if="getItem('organization', item.workshopId).type === 3"
            :to="{name:'workshop', params: {id: item.workshopId}}">
            {{ getValue('organization', item.workshopId) }}
          </router-link>
            <router-link
              v-else
              :to="{name:'warehouse', params: {id: item.workshopId}}">
              {{ getValue('organization', item.workshopId) }}
            </router-link>
          </td>
          <td>{{ item.serial }}</td>
          <td v-if="!hideValues('alerts')">
            <alert-table-item :item="item" />
          </td>
          <td v-if="!hideValues('transmitterId')">
            <router-link
              :to="{ name: 'searchPlug', params: { serial: item.transmitterId, goodsGroup: 3 }} ">
              {{ item.transmitterId }}
            </router-link>
          </td>
          <td v-if="!hideValues('oilLevel')">
            <oil-level-icon
              :level="item.oilLevel"
              :current-volume="item.currentVolume"
            />
          </td>
          <td v-if="!hideValues('oil')">{{ getValue('goods', item.oil) }}</td>
          <td v-if="!hideValues('drum')">{{ getValue('goods', item.drum) }}</td>
          <td v-if="!hideValues('status')">{{ $t(getStatusLabel('devices', item.status)) }}</td>
          <td v-if="!hideValues('lastMeasurement')">{{ dateFormat(item.lastMeasurement) }}</td>
          <td v-if="!hideValues('shipmentDate')">{{ dateFormat(item.shipmentDate) }}</td>
          <td v-if="!hideValues('sufficiency')">{{ getSufficiency(item.sufficiency) }}</td>
          <td v-if="!hideValues('batteryLevel')"><battery-level-icon
            v-if="showBatteryIcon"
            :level="convertBatteryCharge(item.batteryLevel, true)" /><span v-else>{{ convertBatteryCharge(item.batteryLevel, false) }}</span></td>
          <td v-if="!hideValues('connection')">{{ getConnection(item.connection) }}</td>
          <td v-if="userAllowedToCreate">
            <device-table-actions
              :item="item"
              @clicked="handleActionsClicked" />
          </td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)" />
    <adjust-columns-dialog
      v-model="showDialog"
      :headers="headers.filter(h => h.show !== undefined)"
      :model-name="modelName"
      @columns-updated="updateColumns"
    />
    <create-device-dialog
      v-model="showCreateDeviceModal"
      :cloned-item="clonedItem"
      :from-workshop="false"
      @on-created="onCreateHandler"
      @close-clicked="showCreateDeviceModal = false" />
    <edit-device-dialog
      v-if="showEditDeviceModal"
      v-model="showEditDeviceModal"
      :item="clonedItem"
      :old-item="currentItem"
      @on-created="onCreateHandler"
      @close-clicked="showEditDeviceModal = false"/>
    <dialog-ship-confirm
      v-if="currentItem"
      v-model="needConfirm"
      :item="currentItem"
      @on-created="onShipHandler"
      @close-clicked="needConfirm = false" />

  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}

.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
